// shell/components/entity-fields/form-renderers/checkbox-group-input.ts

import { createElement } from '../../../utils/dom';
import type { CheckboxGroupFormField } from '../../entity-view/types';
import type { FormRendererResult } from './types';

/**
 * Renders a fixed set of checkboxes. The bound value is a `string[]`
 * of the checked option keys. When `allowAny` is true (default) a
 * leading "any" checkbox is rendered that represents the empty-array
 * state: checking it clears all other selections; checking any
 * specific option unchecks "any".
 */
export function renderCheckboxGroupInput<T>(
    field: CheckboxGroupFormField<T>,
    initialValue: unknown,
    onChange: (value: string[]) => void,
): FormRendererResult {
    const container = createElement('div', {
        className: 'ev-field-input ev-field-input--checkbox-group',
    });

    const allowAny = field.allowAny !== false;
    const anyLabel = field.anyLabel ?? 'any — no restriction';

    let selected: string[] = Array.isArray(initialValue)
        ? initialValue.map(String)
        : [];

    const checkboxes = new Map<string, HTMLInputElement>();
    let anyCheckbox: HTMLInputElement | null = null;

    function syncUI(): void {
        const isAny = selected.length === 0;
        if (anyCheckbox) anyCheckbox.checked = allowAny && isAny;
        for (const [key, cb] of checkboxes) {
            cb.checked = selected.includes(key);
        }
    }

    function emit(): void {
        syncUI();
        onChange([...selected]);
    }

    if (allowAny) {
        const row = createElement('label', {
            className: 'ev-checkbox-group-item ev-checkbox-group-any',
        });
        anyCheckbox = createElement('input', {
            className: 'ev-checkbox-group-input',
            attributes: {
                type: 'checkbox',
                ...(field.disabled ? { disabled: '' } : {}),
            },
        }) as HTMLInputElement;
        anyCheckbox.addEventListener('change', () => {
            if (anyCheckbox!.checked) {
                // "any" checked — clear all specific selections.
                selected = [];
            } else {
                // Unchecking "any" while nothing else is selected is a
                // no-op: the empty state is "any". Snap back to checked.
                // (A specific checkbox must be toggled instead.)
            }
            emit();
        });
        row.appendChild(anyCheckbox);
        row.appendChild(createElement('span', {
            className: 'ev-checkbox-group-label',
            textContent: anyLabel,
        }));
        container.appendChild(row);
    }

    for (const option of field.options) {
        const key = String(option.key);
        const row = createElement('label', {
            className: 'ev-checkbox-group-item',
        });
        const cb = createElement('input', {
            className: 'ev-checkbox-group-input',
            attributes: {
                type: 'checkbox',
                ...(field.disabled ? { disabled: '' } : {}),
            },
        }) as HTMLInputElement;
        cb.addEventListener('change', () => {
            if (cb.checked) {
                if (!selected.includes(key)) selected.push(key);
            } else {
                selected = selected.filter(k => k !== key);
            }
            emit();
        });
        row.appendChild(cb);
        row.appendChild(createElement('span', {
            className: 'ev-checkbox-group-label',
            textContent: option.label,
        }));
        container.appendChild(row);
        checkboxes.set(key, cb);
    }

    syncUI();

    return {
        element: container,
        getValue: () => [...selected],
        setValue: (value: unknown) => {
            selected = Array.isArray(value) ? value.map(String) : [];
            syncUI();
        },
        validate: async () => true,
        focus: () => {
            const first = anyCheckbox ?? checkboxes.values().next().value;
            first?.focus();
        },
        setDisabled: (disabled: boolean) => {
            if (anyCheckbox) anyCheckbox.disabled = disabled;
            for (const cb of checkboxes.values()) cb.disabled = disabled;
        },
        dispose: () => {
            // Listeners are on elements that get removed with the container.
        },
    };
}
